import React, { useState } from "react";
import { Btn, Field, Input, Select, StatCard, Textarea, Modal, Toast, useToast } from "../components/ui";
import { isCompactViewport, T} from "../lib/globals";
import { createClientId } from "../lib/formatters";
import { makeCreateMeta, makeUpdateMeta } from "../lib/meta";
import { matchesRecordNumberSearch } from "../lib/recordNumberSearch";
import { useSysLists } from "../sync/SyncProvider";

export function createProvidersModule(deps) {
  const {
    useGlobalCollection,
    useSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    PROVIDER_SPECIALTIES,
  } = deps;

  function ProvidersModule({ session, isGestor }) {
  const SL = useSysLists();
  const [providers, setProviders] = useGlobalCollection("providers", []);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");
  const [filterSpec, setFilterSpec] = useState("Todos");
  const [toast, show] = useToast();
  const syncOnSave = useSyncOnSave();
  const canCreate = canCreateInModule(session, "providers") || canEditInModule(session, "providers");
  const canEdit = canEditInModule(session, "providers");
  const canDelete = canDeleteInModule(session, "providers");

  const defForm = {
    _new: true, name: "", specialty: "", phone: "", email: "", whatsapp: "",
    cpfCnpj: "", address: "", city: "", notes: "", rating: 0, active: true,
  };

  function save() {
    if (form?._new && !canCreate) { show("Sem permissão para criar prestadores.", "error"); return; }
    if (!form?._new && !canEdit) { show("Sem permissão para editar prestadores.", "error"); return; }
    if (!form.name.trim()) { show("Nome obrigatório.", "error"); return; }
    if (!form.specialty) { show("Selecione a especialidade.", "error"); return; }
    const { _new, ...d } = form;
    const actorName = session?.name || "";
    if (_new) {
      const clientId = String(d.clientId || createClientId("prov"));
      setProviders(prev => [...prev, {
        ...d,
        id: String(d.id || clientId),
        clientId,
        _deleted:false,
        deletedAt:"",
        deleted_at:"",
        deletedBy:"",
        ...makeCreateMeta(actorName),
      }]);
    } else {
      setProviders(prev => prev.map(x => String(x.id) === String(d.id) ? ({
        ...x,
        ...d,
        clientId: String(x.clientId || d.clientId || x.id || d.id || createClientId("prov")),
        _deleted:false,
        deletedAt:"",
        deleted_at:"",
        deletedBy:"",
        ...makeUpdateMeta(actorName, { deletedAt:"", deleted_at:"", deletedBy:"" }),
      }) : x));
    }
    show("✅ Salvo!");
    setForm(null);
    syncOnSave("providers");
  }

  const filtered = providers
    .filter(p => !(p?._deleted || p?.deletedAt || p?.deleted_at))
    .filter(p => filterSpec === "Todos" || p.specialty === filterSpec)
    .filter(p => matchesRecordNumberSearch(p, search, [p?.name, p?.specialty]));

  const specs = ["Todos", ...new Set(providers.map(p => p.specialty).filter(Boolean))];

  function StarRating({ value, onChange, readonly = false }) {
    return (
      <div style={{ display: "flex", gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <span
            key={n}
            onClick={() => !readonly && onChange && onChange(n)}
            style={{ fontSize: 18, cursor: readonly ? "default" : "pointer", color: n <= value ? "#F59E0B" : "#334155" }}
          >★</span>
        ))}
      </div>
    );
  }

  return (
    <div><Toast toast={toast} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 20 }}>
        <StatCard icon="👷" label="Total" value={providers.length} color={T.accent} />
        <StatCard icon="✅" label="Ativos" value={providers.filter(p => p.active).length} color={T.green} />
        <StatCard icon="⭐" label="Avaliados" value={providers.filter(p => p.rating > 0).length} color={T.yellow} />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <Input placeholder="Buscar prestador ou número..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
        <Select value={filterSpec} onChange={e => setFilterSpec(e.target.value)} style={{ width: 200 }}>
          {specs.map(s => <option key={s}>{s}</option>)}
        </Select>
        {canCreate && <Btn onClick={() => setForm({ ...defForm })}>+ Novo Prestador</Btn>}
      </div>
      {!canCreate && <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>Somente usuários autorizados pelo painel do admin podem cadastrar novos prestadores.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(p => (
          <div key={p.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 18px", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${T.accent}22`, border: `2px solid ${T.accent}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>👷</div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 700, color: T.text, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                {p.name}
                {!p.active && <span style={{ fontSize: 10, background: `${T.red}22`, color: T.red, borderRadius: 4, padding: "1px 6px" }}>Inativo</span>}
              </div>
              <div style={{ fontSize: 12, color: T.accent, marginTop: 2, fontWeight: 600 }}>{p.specialty}</div>
              <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                {p.phone && <span style={{ fontSize: 12, color: T.muted }}>📞 {p.phone}</span>}
                {p.whatsapp && <span style={{ fontSize: 12, color: "#25D366" }}>💬 {p.whatsapp}</span>}
                {p.email && <span style={{ fontSize: 12, color: T.muted }}>✉️ {p.email}</span>}
                {p.city && <span style={{ fontSize: 12, color: T.muted }}>📍 {p.city}</span>}
              </div>
              {p.rating > 0 && <div style={{ marginTop: 4 }}><StarRating value={p.rating} readonly /></div>}
            </div>
            {canEdit && (
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={() => setForm({ ...p, _new: false })}>✏️</Btn>
                <Btn
                  size="sm"
                  variant={p.active ? "danger" : "success"}
                  onClick={() => {
                    if (!canEdit) { show("Sem permissão para editar prestadores.", "error"); return; }
                    setProviders(x => x.map(pp => pp.id === p.id ? { ...pp, active: !pp.active } : pp));
                    syncOnSave("providers");
                  }}
                >
                  {p.active ? "Desativar" : "Ativar"}
                </Btn>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div style={{ color: T.muted, textAlign: "center", padding: 48 }}>Nenhum prestador encontrado.</div>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form ? (form._new ? "Novo Prestador" : "Editar Prestador") : ""} wide>
        {form && <div style={{ display: "grid", gridTemplateColumns: isCompactViewport() ? "1fr" : "1fr 1fr", gap: 14 }}>
          <Field label="Nome completo *" col="1/-1">
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome do prestador ou empresa" />
          </Field>
          <Field label="Especialidade *">
            <Select value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))}>
              <option value="">Selecione...</option>
              {(SL.PROVIDER_SPECIALTIES || PROVIDER_SPECIALTIES).map(s => <option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
              {[true, false].map(v => (
                <div key={String(v)} onClick={() => setForm(f => ({ ...f, active: v }))}
                  style={{ flex: 1, textAlign: "center", padding: "8px", borderRadius: 8, cursor: "pointer", border: `2px solid ${form.active === v ? (v ? T.green : T.red) : T.border}`, background: form.active === v ? (v ? `${T.green}18` : `${T.red}18`) : "transparent", color: form.active === v ? (v ? T.green : T.red) : T.muted, fontWeight: 600, fontSize: 13 }}>
                  {v ? "✅ Ativo" : "⛔ Inativo"}
                </div>
              ))}
            </div>
          </Field>
          <Field label="Telefone"><Input value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(00) 0000-0000" /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp || ""} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="(00) 9 0000-0000" /></Field>
          <Field label="E-mail"><Input type="email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" /></Field>
          <Field label="CPF / CNPJ"><Input value={form.cpfCnpj || ""} onChange={e => setForm(f => ({ ...f, cpfCnpj: e.target.value }))} placeholder="000.000.000-00" /></Field>
          <Field label="Cidade / Região"><Input value={form.city || ""} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Cidade" /></Field>
          <Field label="Endereço" col="1/-1"><Input value={form.address || ""} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Rua, número, bairro" /></Field>
          <Field label="Avaliação" col="1/-1">
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 4 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <span key={n} onClick={() => setForm(f => ({ ...f, rating: n }))} style={{ fontSize: 24, cursor: "pointer", color: n <= (form.rating || 0) ? "#F59E0B" : "#334155" }}>★</span>
                ))}
              </div>
              {(form.rating || 0) > 0 && <Btn size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, rating: 0 }))}>Limpar</Btn>}
            </div>
          </Field>
          <Field label="Observações" col="1/-1">
            <Textarea rows={2} value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observações sobre este prestador..." />
          </Field>
          <div style={{ gridColumn: "1/-1", display: "flex", gap: 10, justifyContent: "space-between" }}>
            <div>{!form._new && canDelete && <Btn variant="danger" onClick={() => { const deletedAt = new Date().toISOString(); setProviders(prev => prev.map(x => String(x.id) !== String(form.id) ? x : ({ ...x, _deleted:true, deletedAt, deleted_at:deletedAt, deletedBy:session?.name || "", ...makeUpdateMeta(session?.name || "", { deletedAt, deleted_at:deletedAt, deletedBy:session?.name || "" }) }))); setForm(null); show("🗑️ Excluído."); syncOnSave("providers"); }}>🗑️ Excluir</Btn>}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setForm(null)}>Cancelar</Btn>
              <Btn onClick={save}>Salvar</Btn>
            </div>
          </div>
        </div>}
      </Modal>
    </div>
  );
}

  return ProvidersModule;
}
